import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Button } from '@mui/material';
import { type GridRenderCellParams } from '@mui/x-data-grid';
import { KeyboardArrowUp } from '@mui/icons-material';
import { Controller, useFieldArray } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useCreateHPO, useGetHpo, useUnAssignHPOPatient } from '@/api/hpo/hpo';
import { getHPOColumns } from './hpo/getHPOColumns';
import BtnMenuHPO from './BtnMenuHPO';
import { HPOSchema } from '@/zod_schemas';
import AssociatedGenesDialog from './AssociatedGenesDialog';
import { usePatientContext } from './usePatientContext';
import APIHPODialog from './APIHPODialog';
import { getErrorMessage } from '@/utils/getErrorMessage';
import DeleteConfirmation from '@/components/Dialog/DeleteConfirmation';
import AppDataGrid from '@/components/DataGrid';

const PatientHPOLists = () => {
  const {
    patientId,
    form: { control },
  } = usePatientContext();

  const [isAddHPOVisible, setIsAddHPOVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<HPOSchema[]>([]);
  const [viewDetails, setviewDetails] = useState<HPOSchema | undefined>(
    undefined,
  );
  const [open, setOpen] = useState(false);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  // Function to open the modal with row details
  const handleOpenDetails = (row: HPOSchema) => {
    setviewDetails(row);
  };

  // Function to close the modal
  const handleClose = () => {
    setviewDetails(undefined);
  };

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const focusSearchField = () => {
    if (searchFieldRef.current) {
      searchFieldRef.current.focus();
    }
  };

  const { data: fetchedData } = useGetHpo(
    debouncedSearchQuery.length >= 3 ? debouncedSearchQuery : undefined,
  );

  const resetFields = () => {
    setSearchQuery('');
    setIsAddHPOVisible(false);
    setSelectedRows([]);
  };

  const createHPO = useCreateHPO(patientId);
  const removeHPOPatient = useUnAssignHPOPatient(patientId);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'patient_hpos',
    keyName: '_key',
  });

  const handleConfirmClick = async () => {
    // Validation check
    if (selectedRows.length === 0) {
      enqueueSnackbar('Please select at least one HPO term', {
        variant: 'warning',
      });
      return;
    }

    try {
      let validData = selectedRows.filter(
        (item): item is HPOSchema => item !== undefined,
      );

      if (patientId) {
        // Add the patient_id field to each object (assign hpo to patient)
        validData = validData.map((hpo) => ({
          ...hpo,
          patient_id: patientId,
        }));

        await createHPO.mutateAsync(validData);

        enqueueSnackbar(
          `Successfully added ${validData.length} HPO term${
            validData.length > 1 ? 's' : ''
          }`,
          {
            variant: 'success',
          },
        );
      } else {
        const normalized = validData.map((hpo) => ({
          ...hpo,
          descendant_count: hpo.descendant_count ?? null,
          genes: hpo.genes ?? [],
        }));

        append(normalized);

        enqueueSnackbar(
          `Successfully added ${normalized.length} HPO term${
            normalized.length > 1 ? 's' : ''
          }`,
          {
            variant: 'success',
          },
        );
      }

      // Reset and close dialog on success
      resetFields();
      setOpen(false);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const handleClearSelection = async () => {
    setSelectedRows([]);
    enqueueSnackbar('Selection cleared', { variant: 'info' });
  };

  // =======================
  // Delete confirmation state
  // =======================
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const openDelete = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const unAssignPatientHPO = useCallback(
    async (normalizationID: string) => {
      setDeletingRowId(normalizationID);

      console.log(normalizationID);

      if (patientId) {
        try {
          await removeHPOPatient.mutateAsync(normalizationID);
          enqueueSnackbar('Successfully removed HPO', { variant: 'success' });
        } catch (ex) {
          enqueueSnackbar('Failed to remove HPO', { variant: 'error' });
        } finally {
          setDeletingRowId(null);
        }
        return;
      }

      const index = fields.findIndex(
        (item) =>
          item.id === normalizationID || item.hpo_id === normalizationID,
      );

      if (index !== -1) {
        remove(index);
        enqueueSnackbar('HPO term removed', { variant: 'success' });
      }

      setDeletingRowId(null);
    },
    [patientId, removeHPOPatient, fields, remove],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    await unAssignPatientHPO(deleteTarget.id);

    closeDelete();
  }, [deleteTarget, closeDelete, unAssignPatientHPO]);

  const columns = getHPOColumns({
    deleteAction: true,
    onClickDelete: (rowId, name) => {
      openDelete(rowId, name);
    },
    deletingRowId,
    isDeleting: removeHPOPatient.isPending,
  });

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchClick = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      startTransition(() => setDebouncedSearchQuery(searchQuery));
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1.5 }}>
        {!isAddHPOVisible && <BtnMenuHPO setOpen={setOpen} />}
      </Box>

      <AssociatedGenesDialog
        viewDetails={viewDetails}
        handleClose={handleClose}
      />
      <Controller
        name="patient_hpos"
        control={control}
        render={({ field }) => {
          const rows = field.value || [];

          return (
            <AppDataGrid
              key={rows.length} //  Force re-render when rows change
              rows={rows}
              columns={[
                {
                  field: 'details',
                  headerName: 'Details',
                  width: 100,
                  renderCell: (params: GridRenderCellParams) => (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpenDetails(params.row)}
                      sx={{ minWidth: 40 }}
                      disabled={removeHPOPatient.isPending}
                    >
                      <KeyboardArrowUp />
                    </Button>
                  ),
                },
                ...columns,
              ]}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              sx={{
                height: 400,
                '& .MuiDataGrid-cell': {
                  alignItems: 'flex-start',
                  py: 1,
                },
                '& .MuiDataGrid-row': {
                  opacity: removeHPOPatient.isPending ? 0.6 : 1,
                },
              }}
              pageSizeOptions={[5]}
              checkboxSelection
              disableRowSelectionOnClick
              getRowId={(row) => row.hpo_id ?? row.id}
              loading={removeHPOPatient.isPending}
            />
          );
        }}
      />

      <APIHPODialog
        open={open}
        setOpen={setOpen}
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearchClick={handleSearchClick}
        searchFieldRef={searchFieldRef}
        focusSearchField={focusSearchField}
        handleConfirmClick={handleConfirmClick}
        selectedRows={selectedRows}
        handleClearSelection={handleClearSelection}
        fetchedData={fetchedData}
        setSelectedRows={setSelectedRows}
        isSubmitting={createHPO.isPending}
      />
      <DeleteConfirmation
        open={Boolean(deleteTarget)}
        itemKey={deleteTarget?.name}
        confirmText="delete"
        onClose={closeDelete}
        onDelete={confirmDelete}
        hideArchive
        isDeleting={Boolean(deletingRowId) || removeHPOPatient.isPending}
      />
    </Box>
  );
};

export default PatientHPOLists;
